import { Moment } from 'moment';
import {
    AlertGridV2,
    ComparisonOperator,
    GridFormat,
    GroupedGrid,
    LookupItem,
    MetricItem,
    PrimaryTierMap,
    PcodePrimaryTierMap,
    StringLookupItem,
    SegmentItem,
    PoliticalGroupedGrid,
    PoliticalTierLookupItem,
} from '@models/lookup';
import { MatDialog } from '@angular/material/dialog';
import { Column } from 'ag-grid-community';

export interface GroupedMetricItem {
    id: string;
    displayName: string;
    selected: boolean;
    isFavorite?: boolean;
    group?: string;
    marketId?: number;
}

export interface MetricsOrderTypedItem {
    id: string;
    index: number;
}

export interface Group {
    groupName: string;
    metrics: GroupedMetricItem[];
}

export interface GroupData {
    [groupName: string]: Group;
}

export interface RateCardMetricData {
    [metricName: string]: RateCardMetricItem;
}

export interface RateCardMetricItem extends MetricItem {
    isOverrideable: boolean;
    isDefault: boolean;
    displayName: string;
    isPrimary?: boolean;
    isFavorite?: boolean;
    metricType: string;
    spotLengthId: number;
    demoCode: string;
    group: string;
}

export interface RateCardStationOrder extends RateCardMetricItem {
    marketId: number;
}

export interface EnabledRatecard {
    name: string;
    displayName: string;
    isSelected?: boolean;
}

export interface EnabledRatecards {
    rateCard?: EnabledRatecard;
    priorityCodeRatecard?: EnabledRatecard;
}

export interface RangeOverrideTypes {
    name: string;
    sourceMetricId?: string;
    type: 'reset' | 'modify' | 'clear' | 'approve';
    valueType?: 'manual' | 'auto';
    operation?: '+' | '*' | '#';
    divisor?: number;
    adder?: number;
    overrideType: '$' | '%';
    isFloating?: boolean;
    sortOrder?: number;
}

export interface RateCardMetricValueV2 {
    collapseOverride?: boolean;
    isEditable: boolean;
    rateCardMetricValueId: string;
    rateCardMetricValueIds: number[];
    divisor: number;
    multiplier: number;
    isIssue?: boolean;
    overrideData?: RateCardMetricOverrideData;
    displayTooltip?: string;
    isPrimary?: boolean;
    value: {
        current: number;
        original: number;
        session_start: number;
        political_rate?: number;
        is_political_row_hidden?: boolean;
    };
    alerts?: { [alertName: string]: number };
    periodStartDate: string;
    periodEndDate?: string;
    isPast?: boolean;
    isPcode?: boolean;
    isSpecialEvent?: boolean;
    isFillIn?: boolean;
    sessionRevertedToPrevious?: boolean;
    isTotal?: boolean;
    tierOverridePriceMax?: number;
    tierOverridePriceFloor?: number;
    windowId?: number;
}

export interface DynamicRateCardMetricValueV2 extends RateCardMetricValueV2 {
    pricingTier: string;
    quarterName?: string;
    metricName?: string;
    overrideBounded?: boolean;
    isPoliticalBound?: boolean;
}

export interface PoliticalRateCardMetricValueV2 extends RateCardMetricValueV2 {
    politicalTier: string;
}

export interface DigitalRateCardMetricValueV2 extends RateCardMetricValueV2 {
    pricingTier: string;
    metricName?: string;
    overrideBounded?: boolean;
}

export interface RateCardMetricOverrideData {
    name: string;
    state: string;
    time: string;
    original?: number;
    period: string;
    metric_name: string;
    overrideType?: '$' | '%';
    isFloating?: boolean;
    recValue?: number;
    isApproved?: boolean;
    approvedByUser?: string;
    approvedTimestamp?: string;
    isSystemGenerated?: boolean;
}

export interface RateCardMetricOverrideGroup {
    editedValues: RateCardMetricOverrideMap;
    deletedValues: RateCardMetricOverrideMap;
    approvedValues: RateCardMetricOverrideMap;
}

export interface RateCardMetricOverrideMap {
    [channelId: number]: { [k: string]: RateCardMetricOverrideV2 };
}

export interface RateCardMetricOverrideV2 {
    rateCardId: number;
    rateCardMetricValueIds: string;
    metricName?: string;
    channelId: number;
    tierId: string;
    date: string;
    overrideType?: string;
    overrideValue?: number;
    isSpecialEvent?: boolean;
    recValue?: number;
    isFloating?: boolean;
    isTierCascade?: boolean;
    isPcode?: boolean;
    isIssue?: boolean;
    isOverrideTriggerRecalc?: boolean;
}

export interface RateCardMetricRowV2<T extends RateCardMetricValueV2 = RateCardMetricValueV2> {
    channel: string;
    channelId: number;
    channelSortOrder?: number;
    stationSortOrder?: number;
    daypart?: string | RateCardDaypart;
    daypartId?: number;
    format: {
        format: string;
        type: string;
    };
    metricName: string;
    rateCardMetricId: string;
    isUserGenerated: boolean;
    metricTypeId: string;
    spotType: string;
    spotTypeId: number;
    rateCardId: string;
    rateCardIdInt: number;
    segmentation: {
        [segmentType: string]: RateCardSegment;
    };
    segmentationIds: { [segmentType: string]: number };
    value: {
        [k: string]: T;
    };
    sortOrder: number;
    isTiered: boolean;
    isOverrideTriggerRecalc: boolean;
    isPolitical?: boolean;
    market?: string;
    station?: string;
    marketId?: number;
    stationId?: number;
    specialEventType?: LookupItem;
    alerts?: { [alertName: string]: number };
    overridePriceFloor?: number;
    overridePriceMax?: number;
    digitalProductId?: number;
    digitalProductTypeId?: number;
    firstName?: string;
    lastName?: string;
    createdDate?: string;
    hiatusWeeks?: string[];
    actionField?: string;
    selectRow?: boolean;
    uniqueRowId?: string;
    uniqueRowValues?: string;
    mergedSegmentId?: number;
    isMergedSegment?: boolean;
    periodStartDate?: string;
    periodEndDate?: string;
    subtitle?: RateCardSegment;
    advertiserCount?: number;
}

export const SUBTITLE_FIELD = 'subtitle';

// subtitle has no traffic system value until the dataload populates one, unlike every other segment
export const EMPTY_SUBTITLE_LABEL = '(empty)';

export interface SaveSubtitleRequest {
    channelId: number;
    baseSegmentationId: number;
    periodStartDate: string;
    subtitle: string;
}

export interface SaveSubtitleResponse {
    channelId: number;
    baseSegmentationId: number;
    periodStartDate: string;
    value: string | null;
    editInfo: RateCardSegment['editInfo'] | null;
}

export interface AdvertiserRow {
    advertiserName: string;
    agencyName: string;
    priorityCodes: string[];
    airWeeks: string;
    spotLength: string;
    units: number;
    aur: number;
    grossSpend: number;
    orderedAs: string | null;
}

export interface TierRow {
    tierId: number;
    tierName: string;
    isPrimary?: boolean;
    multiplierValue?: number;
}

export interface MergedSegmentSourceColumn {
    name: string;
    field: string;
    sortable: boolean;
    display: boolean;
    lockVisible: boolean;
    is_dow?: boolean;
    is_start_time?: boolean;
    is_end_time?: boolean;
}

export interface MergedSegmentSourceRow {
    sourceBaseSegmentationId: number | null;
    daypart: string;
    daypartId: number;
    segmentation: { [segmentType: string]: string };
}

export interface MergedSegmentSourcesResponse {
    columns: MergedSegmentSourceColumn[];
    rows: MergedSegmentSourceRow[];
}

export type DaypartTierRow = {
    daypartId: number;
    daypartName: string;
} & {
    [tierMultiplier: string]: number | string;
};

export interface RateCardSegment {
    value: string;
    editInfo?: {
        name: string;
        originalName: string;
        currentName: string;
        timestamp: string;
    };
    format: GridFormat;
}
export interface RateCardDaypart {
    value: string;
    editInfo?: {
        name: string;
        originalName: string;
        currentName: string;
        timestamp: string;
        originalDaypartId: number;
        overrideDaypartId: number;
        editOnDynamicRateCard: boolean;
    };
    format: GridFormat;
}

export interface WomsMappings {
    updateTimeStamp: Moment;
    mappings: WomsMapping[];
}

export interface WomsProgram {
    id: string;
    name: string;
}

export interface RateOpticsProgram {
    segmentation: {
        [segment: string]: number;
    };
    daypartId: number;
    channelId: number;
    name: string;
}

export interface WomsRateCardRow extends WomsProgram {
    weeklyRates: WeeklyRates[];
}

export interface WomsExport {
    unmappedRateOpticsPrograms: RateOpticsProgram[];
    tiers: {
        [tier: string]: WomsRateCardRow[];
    };
}

export interface WeeklyRates {
    week: string;
    metricName: string;
    metricValue: number;
}

export interface WomsMapping {
    id?: number;
    womsProgram: WomsProgram;
    rateOpticsProgram: RateOpticsProgram | null;
    days?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
}

export interface RateOpticsProgramLookupItem extends LookupItem {
    segmentation: { [segment: string]: number };
    daypartId: number;
    channelId: number;
}

export interface WomsProgramRow {
    womsProgram: WomsProgram;
    rateOpticsProgram?: RateOpticsProgramLookupItem;
    days?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
}

export interface AsOfDate {
    lastDataUpdate: string;
    stationExceptions: boolean;
}

export interface RateCardPdfExportOptions {
    tierOptions: string[];
    metricOptions: StringLookupItem[];
    selectedPrimaryMetric: string;
}

export interface RateCardExcelExportOptions {
    filterOptions: FilterOption[];
    enabledRatecards: EnabledRatecards;
    isExperimental: boolean;
    showAlerts: boolean;
}

export interface FilterOption {
    name: string;
    disabled?: boolean;
    filterFunc: () => boolean;
}

export interface RateCardMetricAlerts {
    metricAlertIds: string[];
    name: string;
    displayName?: string;
    highlightMetric: string;
    color: string;
    buttonMetric?: string;
    hasButton?: boolean;
    highlightOnlyWhenSelected?: boolean;
    displayOrder?: number;
}

export interface RateCardApiMetricAlerts {
    metricAlertIds: { [metricAlertId: string]: number[] };
    comparisonOperators?: { [metricAlertId: string]: string };
    name: string;
    displayName?: string;
    highlightMetric: string;
    color: string;
}

export interface RateCardMetricAlertCalculations {
    alertId: string;
    primaryMetric: string;
    secondaryMetric: string;
    isConfigurable: boolean;
    negativeThreshold: boolean;
    comparisonOperator: ComparisonOperator;
    thresholds: number[];
    isApproved: boolean;
    alertNull: boolean;
    compareOverride: boolean;
    compareRecValue: boolean;
    alertGroup: string;
    useOriginalPrimary: boolean;
    useOriginalSecondary: boolean;
    tiers: string[];
}

export interface DynamicRateCardApiGrid<T, U> extends AlertGridV2<T, U> {
    tiers: RateCardPricingTier[];
    primaryTierMap?: PrimaryTierMap;
    pcodePrimaryTierMap?: PcodePrimaryTierMap;
}

export interface PoliticalRateCardApiGrid<T, U> extends AlertGridV2<T, U> {
    tiers: PoliticalTierLookupItem[];
    showPoliticalArrow: boolean;
}

export interface RateCardPricingTier {
    channel_id: number;
    display_name: string;
    is_primary: boolean;
    lur_display: boolean;
    name: string;
    pricing_tier_id: string;
    pricing_tier_v2_id: number;
    is_default: boolean;
}

export interface DigitalPricingTier {
    can_edit: boolean;
    channel_id: number;
    display_name: string;
    is_primary: boolean;
    name: string;
    pricing_tier_id: number;
    is_default: boolean;
}

export interface RoundingRules {
    [metricName: string]: ModuleRoundingRules;
}

export type ModuleRoundingRules = Partial<Record<'dynamic' | 'political', RoundingRule[]>>;

export interface RoundingRule {
    lowerBound: number;
    upperBound: number;
    roundValue: number;
}

export interface DynamicRateCardGrid<T> extends GroupedGrid<T> {
    tiers: RateCardPricingTier[];
}

export interface DigitalRateCardGrid<T> extends GroupedGrid<T> {
    tiers: DigitalPricingTier[];
}

export interface PoliticalRateCardGrid<T> extends PoliticalGroupedGrid<T> {
    tiers: PoliticalTierLookupItem[];
    showPoliticalArrow: boolean;
}

export interface RateCardTier extends LookupItem {
    isPrimary: boolean;
    tierUsed?: string;
    multiplier?: number;
    lowestTier?: boolean;
    isIssue?: boolean;
}

export interface PoliticalCascadeFactors {
    [windowId: string]: {
        [channelId: string]: {
            [sourceTier: string]: {
                [politicalTierId: string]: number;
            };
        };
    };
}

export interface RateCardTierSettings {
    [channelId: number]: {
        [daypartId: number]: {
            [spotTypeId: number]: {
                [tierID: string]: {
                    isDynamicFloor: boolean;
                    percentOfPrimary: number;
                    isPrimary?: boolean;
                    displayName?: string;
                    isIssue?: boolean;
                };
            };
        };
    };
}

export enum PriorityCodeAlertType {
    alwaysOnAlert,
    upperBoundAlert,
}

export interface PoliticalRateCardPopupReturn {
    markets: LookupItem[];
    channelGroups: number[];
    periodLevel: string;
    stations: LookupItem[];
    politicalWindowNames: string[];
}

export interface BulkOverridesCellToUpdate<T> {
    row: RateCardMetricRowV2;
    values: { [key: string]: T };
    field: string;
    isPrimary: boolean;
    isPcode: boolean;
}

export interface NewDynamicSegments {
    segmentType: string;
    segmentId?: number;
    segmentValue: string;
    startTime?: string;
    endTime?: string;
}

export interface NewSegmentData {
    daypartId: number;
    rate?: number;
    // segments can either be done through "create new" or selecting an existing segment
    // create new will not send an ID but a value, while copying a segment will send all
    dynamicSegments: NewDynamicSegments[];
    isOneTimeSegment: boolean;
    oneTimeSegment?: {
        date: string;
    };
    recurringSegment?: {
        startDate: string;
        endDate: string;
        dows: number[];
        hiatusWeeks: string[];
    };
    requestId?: number;
}

export interface NewSegmentMultiStationData {
    daypartId: number;
    rate: number;
    isOneTimeSegment: boolean;
    dynamicSegments: NewDynamicSegments[];
    channels: number[];
    rateCardTab: number;
    isDynamic: boolean;
    isPolitical: boolean;
    spotTypeId: number;
    startDate: string;
    reloadParams: object;
    parentSegmentData: object;
    endDate?: string;
    dows?: number[];
    oneTimeSegment?: {
        date: string;
    };
    requestId?: number;
    hiatusWeeks?: string[];
}

export interface NewBulkSegmentData {
    bulkRequestId?: number;
    channels: number[];
    daypartId: number;
    isOneTimeSegment?: boolean;
    dynamicSegments: NewDynamicSegments[];
    startDate: string;
    endDate: string;
    dows?: number[];
    hiatusWeeks: string[];
    rateCardTab: number;
    spotTypeId: number;
    isDynamic: boolean;
    isPolitical: boolean;
    liveProgram: boolean;
    marketNames?: string[];
    channelNames?: string[];
    dynamicSegmentation?: { [segmentType: string]: SegmentItem };
    parentDynamicSegmentation?: { [key: string]: number };
    parentDaypartId?: number;
    channelIdRequestIds?: { channelId: number; requestId: number }[];
    rate?: number;
}

interface SegmentPopup {
    rateCardTab: number;
    isDynamic: boolean;
    isPolitical: boolean;
    spotTypeId: number;
    parentSegmentData: {
        daypartId: number;
        dynamicSegmentation: { [segmentType: string]: number };
    };
    newSegments: NewSegmentData[];
}

export interface AddSegmentPopupReturn extends SegmentPopup {
    reloadParams: {
        startDate: string;
        endDate: string;
        period: number;
    };
    channelId: number;
}

export interface BulkUpdateSegmentPopupData extends SegmentPopup {
    requestIds: number[];
    liveProgram: boolean;
    bulkRequestId: number;
}

export interface BulkUserSegment {
    bulkRequestId?: number;
    channels: number[];
    markets: string[];
    daypartId?: number;
    isPolitical?: boolean;
    isOneTimeSegment?: boolean;
    dynamicSegmentation?: { [segmentType: string]: SegmentItem };
    startDate: string;
    endDate: string;
    dows?: number[];
    hiatusWeeks?: string[];
    rate?: number;
    liveProgram?: boolean;
}
export interface EditableSegment {
    requestId?: number;
    daypartId?: number;
    isOneTimeSegment?: boolean;
    dynamicSegmentation?: { [segmentType: string]: SegmentItem };
    parentSegmentData: {
        daypartId: number;
        dynamicSegmentation: { [segmentType: string]: number };
    };
    rate?: number;
    oneTimeSegment?: {
        date: string;
    };
    recurringSegment?: {
        startDate: string;
        endDate: string;
        dows: number[];
        hiatusWeeks: string[];
    };
    isPolitical?: boolean;
    isDynamic?: boolean;
}

export interface MergeSegmentData {
    userSegment: MergeSegmentItem;
    systemSegment: MergeSegmentItem;
}

export interface MergeSegmentItem {
    channelId: number;
    baseSegmentationId: number;
    segmentation: { [key: string]: string };
    segmentationIds: { [key: string]: number };
    startDate: string;
    endDate: string;
    hiatusWeeks: string[];
    rate: number;
    dateCount: number;
    uniqueRowValues?: string;
    daypartId: number;
}

export interface SegmentPopupProps {
    rateCardTab: number;
    channelGroupId: number;
    channelId: number;
    spotTypeId: number;
    daypartId: number;
    priceFloor: number;
    priceMax: number;
    appMetricValueIds: number[];
    primarySpotLength: number;
    primaryTier: string;
    isDynamic: boolean;
    isPolitical: boolean;
    isSport?: boolean;
    parentDynamicSegmentation?: { [segmentType: string]: number };
    getRateCardSegments: () => void;
    afterSave: (props: RateCardMetricRowV2[]) => void;
    matDialog: MatDialog;
    reloadParams: {
        startDate: string;
        endDate: string;
        period: number;
        politicalWindows?: string[];
    };
    marketId: number;
    stationId: number;
}

export interface SystemSegmentPopupProps {
    channelId: number;
    segmentation: { [segmentType: string]: number };
    afterSave: (rows: EditSegmentNameParams[], reloadRows: RateCardMetricRowV2[], subtitle?: SaveSubtitleResponse) => void;
    getRateCardSegments: () => void;
    matDialog: MatDialog;
    daypartId: number;
    spotTypeId?: number;
    baseSegmentationId?: number;
    reloadParams: {
        startDate: string;
        endDate: string;
        period: number;
    };
    rateCardTab: number;
    isPolitical: boolean;
    daypart?: {
        value: string;
        editInfo?: {
            name: string;
            originalName: string;
            currentName: string;
            timestamp: string;
            originalDaypartId: number;
            overrideDaypartId: number;
            editOnDynamicRateCard: boolean;
        };
        format: GridFormat;
    };
    periodStartDate?: string;
    subtitle?: RateCardSegment;
}

export interface SystemSegment {
    originalName: string;
    segmentId: number;
    segmentType: string;
    channelId: number;
    currentName?: string;
    editOnDynamicRateCard?: boolean;
}

export interface EditSegmentNameParams {
    name: string;
    originalName: string;
    currentName: string;
    timestamp: string;
    segmentType: string;
    segmentId: number;
    channelId: number;
    segmentName: string;
    baseSegmentationId: number;
}

export interface HiatusWeekSelection {
    isAired: boolean;
    week: string;
}

export interface ProcessUnpinnedColumnsParams<Columns = Column[]> {
    columns: Columns;
}
